import { 
  StarIcon, 
  CheckCircleIcon, 
  ExclamationTriangleIcon, 
  ExclamationCircleIcon 
} from "@heroicons/react/20/solid";
import { CandidateCvFlagResult } from "../../api/candidates.api";

// ─── Small reusable section card ───────────────────────────────────────────────
function SectionCard({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className=" p-2 bg-white dark:bg-darkbglight rounded-xl shadow-sm border border-gray-100 dark:border-gray-700">
      <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700">
        <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
          {title}
        </h3>
      </div>
      <div className="px-6 py-5">{children}</div>
    </div>
  );
}

interface CVFlagsCardProps {
  cvFlagResult: CandidateCvFlagResult | null;
  isFetchingCvFlags: boolean;
  cvFlagError: string | null;
  handleGetCvFlags: () => void;
}

export default function CVFlagsCard({
  cvFlagResult,
  isFetchingCvFlags,
  cvFlagError,
  handleGetCvFlags,
}: CVFlagsCardProps) {
  return (
    <SectionCard title="CV Flags">
      <div className="flex flex-col gap-4">
        {/* Top Header Row for internal content: Stats + Button */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-6">
            <div className="flex flex-col">
              <span className="text-xs font-semibold text-gray-500 dark:text-gray-500 uppercase tracking-wider mb-1">
                Score
              </span>
              <span className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                {cvFlagResult?.score ?? "N/A"}
              </span>
            </div>
            <div className="w-px h-10 bg-gray-200 dark:bg-gray-700" />
            <div className="flex flex-col">
              <span className="text-xs font-semibold text-gray-500 dark:text-gray-500 uppercase tracking-wider mb-1">
                Tier
              </span>
              <span className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                {cvFlagResult?.tier || "N/A"}
              </span>
            </div>
          </div>
          
          <button
            type="button"
            onClick={handleGetCvFlags}
            disabled={isFetchingCvFlags}
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-entntblue px-4 py-2 text-sm font-medium text-white hover:bg-entntblue/90 disabled:cursor-not-allowed disabled:opacity-60 transition-colors shadow-sm"
          >
            <StarIcon className="h-4 w-4" />
            {isFetchingCvFlags ? "Analyzing CV..." : "Flag CV"}
          </button>
        </div>

        {cvFlagError && (
          <div className="rounded-xl bg-red-50 p-4 text-sm text-red-600 dark:bg-red-900/20 dark:text-red-400 border border-red-100 dark:border-red-800/30">
            {cvFlagError}
          </div>
        )}

        {cvFlagResult && (
          <>
            {cvFlagResult.summary && (
              <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed border-l-2 border-gray-200 dark:border-gray-700 pl-4 py-1">
                {cvFlagResult.summary}
              </p>
            )}

            <div className="border-t border-gray-100 dark:border-gray-700" />

            <div>
              {cvFlagResult.flags?.length ? (
                <div className="space-y-4">
                  <div className="flex items-center gap-2 text-amber-700 dark:text-amber-400 font-medium text-sm bg-amber-50 dark:bg-amber-900/20 px-3 py-2 rounded-lg border border-amber-100 dark:border-amber-900/30 w-fit">
                    <ExclamationTriangleIcon className="h-5 w-5" />
                    <span>{cvFlagResult.flags.length} flag{cvFlagResult.flags.length > 1 ? 's' : ''} require attention</span>
                  </div>
                  <ul className="space-y-3">
                    {cvFlagResult.flags.map((flag, idx) => (
                      <li
                        key={`${flag.flag_title}-${idx}`}
                        className="flex items-start gap-3 rounded-xl border border-gray-200 p-3 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/40 hover:bg-white dark:hover:bg-gray-800 transition-colors"
                      >
                        <div className="mt-0.5 flex-shrink-0 bg-white dark:bg-gray-700 rounded-full p-1 shadow-sm border border-gray-100 dark:border-gray-600">
                          <ExclamationCircleIcon className="h-4 w-4 text-amber-500" />
                        </div>
                        <div>
                          <p className="font-semibold text-gray-900 dark:text-gray-100 text-sm">
                            {flag.flag_title}
                          </p>
                          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                            {flag.reason}
                          </p>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : (
                <div className="flex items-center gap-2 text-green-700 dark:text-green-400 font-medium text-sm bg-green-50 dark:bg-green-900/20 px-4 py-3 rounded-xl border border-green-100 dark:border-green-900/30">
                  <CheckCircleIcon className="h-5 w-5" />
                  <span>No flags found. The candidate's CV looks good!</span>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </SectionCard>
  );
}
